import {
  createHintEscalationState,
  createRetryFeedback,
  createSuccessFeedback,
  type ActivityFeedback,
  type HintEscalationState
} from "../../feedback";
import type { InputMethod } from "../../input";
import type {
  DragOrganizePayload,
  DragOrganizePlacement
} from "./schema";

export interface DragOrganizeState {
  readonly placements: readonly DragOrganizePlacement[];
  readonly selectedItemId: string | null;
  readonly attemptCount: number;
  readonly correctAttemptCount: number;
  readonly retryCount: number;
  readonly hintState: HintEscalationState;
  readonly feedback: ActivityFeedback | null;
  readonly completed: boolean;
}

export type DragOrganizeAction =
  | {
      readonly type: "pointer-drop";
      readonly itemId: string;
      readonly dropZoneId: string;
    }
  | {
      readonly type: "select-item";
      readonly itemId: string;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "select-drop-zone";
      readonly dropZoneId: string;
      readonly inputMethod: InputMethod;
    }
  | { readonly type: "cancel-selection" }
  | { readonly type: "request-hint" }
  | { readonly type: "undo" };

export interface DragOrganizeScore {
  readonly points: number;
  readonly correctRate: number;
  readonly completionRate: number;
  readonly attemptCount: number;
  readonly correctAttemptCount: number;
  readonly retryCount: number;
  readonly hintCount: number;
  readonly completed: boolean;
}

export function createDragOrganizeState(): DragOrganizeState {
  return {
    placements: [],
    selectedItemId: null,
    attemptCount: 0,
    correctAttemptCount: 0,
    retryCount: 0,
    hintState: createHintEscalationState(),
    feedback: null,
    completed: false
  };
}

function hasItem(payload: DragOrganizePayload, itemId: string): boolean {
  return payload.items.some((item) => item.id === itemId);
}

function hasDropZone(
  payload: DragOrganizePayload,
  dropZoneId: string
): boolean {
  return payload.dropZones.some((dropZone) => dropZone.id === dropZoneId);
}

function isItemPlaced(state: DragOrganizeState, itemId: string): boolean {
  return state.placements.some((placement) => placement.itemId === itemId);
}

function getCorrectDropZoneId(
  payload: DragOrganizePayload,
  itemId: string
): string | undefined {
  return payload.correctPlacements.find(
    (placement) => placement.itemId === itemId
  )?.dropZoneId;
}

function tryPlacement(
  state: DragOrganizeState,
  payload: DragOrganizePayload,
  itemId: string,
  dropZoneId: string
): DragOrganizeState {
  if (
    state.completed ||
    !hasItem(payload, itemId) ||
    !hasDropZone(payload, dropZoneId) ||
    isItemPlaced(state, itemId)
  ) {
    return state;
  }

  const attemptCount = state.attemptCount + 1;

  if (getCorrectDropZoneId(payload, itemId) !== dropZoneId) {
    const retry = createRetryFeedback(state.hintState);

    return {
      ...state,
      selectedItemId: null,
      attemptCount,
      retryCount: state.retryCount + 1,
      hintState: retry.hintState,
      feedback: retry.feedback
    };
  }

  const placements = [...state.placements, { itemId, dropZoneId }];

  return {
    ...state,
    placements,
    selectedItemId: null,
    attemptCount,
    correctAttemptCount: state.correctAttemptCount + 1,
    feedback: createSuccessFeedback(),
    completed: placements.length === payload.items.length
  };
}

export function reduceDragOrganize(
  state: DragOrganizeState,
  payload: DragOrganizePayload,
  action: DragOrganizeAction
): DragOrganizeState {
  switch (action.type) {
    case "pointer-drop":
      return tryPlacement(
        state,
        payload,
        action.itemId,
        action.dropZoneId
      );
    case "select-item":
      if (
        state.completed ||
        !hasItem(payload, action.itemId) ||
        isItemPlaced(state, action.itemId) ||
        state.selectedItemId === action.itemId
      ) {
        return state;
      }

      return {
        ...state,
        selectedItemId: action.itemId,
        feedback: null
      };
    case "select-drop-zone":
      if (state.selectedItemId === null) {
        return state;
      }

      return tryPlacement(
        state,
        payload,
        state.selectedItemId,
        action.dropZoneId
      );
    case "cancel-selection":
      if (state.selectedItemId === null) {
        return state;
      }

      return {
        ...state,
        selectedItemId: null
      };
    case "request-hint": {
      if (state.completed) {
        return state;
      }

      const retry = createRetryFeedback(state.hintState);

      return {
        ...state,
        hintState: retry.hintState,
        feedback: retry.feedback
      };
    }
    case "undo": {
      if (state.placements.length === 0) {
        return state;
      }

      return {
        ...state,
        placements: state.placements.slice(0, -1),
        selectedItemId: null,
        attemptCount: Math.max(0, state.attemptCount - 1),
        correctAttemptCount: Math.max(0, state.correctAttemptCount - 1),
        feedback: null,
        completed: false
      };
    }
  }
}

function clampUnit(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function calculateDragOrganizeScore(
  state: DragOrganizeState,
  payload: DragOrganizePayload
): DragOrganizeScore {
  const correctRate =
    state.attemptCount === 0
      ? 0
      : clampUnit(state.correctAttemptCount / state.attemptCount);
  const completionRate = clampUnit(
    state.placements.length / payload.items.length
  );

  return {
    points: Math.round(correctRate * completionRate * 100),
    correctRate,
    completionRate,
    attemptCount: state.attemptCount,
    correctAttemptCount: state.correctAttemptCount,
    retryCount: state.retryCount,
    hintCount: state.hintState.hintCount,
    completed: state.completed
  };
}
